import React, { useContext, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Translations } from '../../services/TranslationService.jsx'
import useSectionObserver from '../../hooks/useSectionObserver.jsx'
import Icons from '@3mcom/mds-library/dist/node/Icons'
import { StateContext } from '../../services/StateService.jsx'
import { trackPDPData } from '../../services/TealiumService.jsx'
import scrollService from '../../services/ScrollService.jsx'
import '../../css/JumpMenu.scss'

const JumpMenu = ({ sections, className = '' }) => {
    const translations = useContext(Translations)
    const { productDetails } = useContext(StateContext)

    const navRef = useRef(null)

    const sectionIds = sections.map((section) => section.id)
    const { activeSection, isNavSticky, scrollToTop, initializeStickyDetection } =
        useSectionObserver(sectionIds)

    useEffect(() => {
        if (navRef.current) {
            const cleanup = initializeStickyDetection(navRef.current)
            return cleanup
        }
    }, [initializeStickyDetection])

    useEffect(() => {
        if (navRef.current) {
            scrollService.registerNav(navRef, isNavSticky, 100)

            const cleanup = initializeStickyDetection(navRef.current)
            return cleanup
        }
    }, [initializeStickyDetection, isNavSticky])

    const handleNavClick = (section) => {
        scrollService.scrollToSection(section.id)
        trackPDPData(
            `PDP Sticky Navigation Button Click: ${section.label}`,
            'Engagement Event'
        )
        if (section.onClick) section.onClick(section.id)
    }

    const handleScrollToTop = () => {
        scrollToTop()
        trackPDPData(
            `PDP Sticky Navigation Button Click: ${translations?.pageTop}`,
            'Engagement Event'
        )
    }

    const navClasses = `sps2-pdp_jumpMenu ${
        isNavSticky ? 'sps2-pdp_jumpMenu--sticky' : ''
    } ${className}`.trim()

    return (
        <nav ref={navRef} className={navClasses} aria-label={productDetails?.name}>
            {isNavSticky && (
                <h2 className="mds-font_body sps2-pdp_jumpMenu--header">
                    {productDetails?.name}
                </h2>
            )}
            <ul className="sps2-pdp_jumpMenu--navList" role="menubar">
                {sections.map((section) => (
                    <li
                        key={section.id}
                        className="sps2-pdp_jumpMenu--navList-item"
                        role="none"
                    >
                        <button
                            className={`sps2-pdp_jumpMenu--navList-item_link ${
                                activeSection === section.id
                                    ? 'sps2-pdp_jumpMenu--navList-item_link-active'
                                    : ''
                            }`}
                            onClick={() => handleNavClick(section)}
                            data-testid={`nav-button-${section.id}`}
                            role="menuitem"
                        >
                            <span className="sps2-pdp_jumpMenu--navList-item_link-text">
                                {section.label}
                            </span>
                        </button>
                    </li>
                ))}
                {/* Scroll to top button */}
                {isNavSticky && (
                    <li className="sps2-pdp_jumpMenu--navList-item" role="none">
                        <button
                            className="sps2-pdp_jumpMenu--navList-item_topButton"
                            onClick={handleScrollToTop}
                            aria-label={translations?.pageTop}
                            title={translations?.pageTop}
                        >
                            <Icons.icon_chevron_up_black
                                fill="white"
                                className="sps2-pdp_jumpMenu--navList-item_topButton--icon"
                            />
                            <span className="mds-font_header--6 sps2-pdp_jumpMenu--navList-item_topButton--label">
                                {translations?.pageTop}
                            </span>
                        </button>
                    </li>
                )}
            </ul>
        </nav>
    )
}

JumpMenu.propTypes = {
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            onClick: PropTypes.func
        })
    ).isRequired,
    className: PropTypes.string,
    observerOptions: PropTypes.object
}

export default JumpMenu
